import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { formatNumber } from '../admin/format';
import { isKnownLimitKey } from './plan-limits';
import { usePlanLimitStore } from './plan-limit-store';
import { useUpgrade } from './useUpgrade';

/**
 * "You've reached the Free limit for X", said the same way everywhere.
 *
 * Mounted once, inside the router. It shows whatever the store was told —
 * by the request layer when the API refused with a quota payload, or by a
 * page that already knew (an add button at the limit).
 */
export function PlanLimitDialog() {
  const { t } = useTranslation();
  const upgrade = useUpgrade();
  const { planName, isFree } = useVendorPlan();
  const current = usePlanLimitStore((s) => s.current);
  const close = usePlanLimitStore((s) => s.close);

  const key = current?.quotaType;
  const known = isKnownLimitKey(key);
  const what = known ? t(`FREE_PLAN.WHAT.${key}`) : t('FREE_PLAN.WHAT.GENERIC');
  const proGets = known ? t(`FREE_PLAN.PRO_GETS.${key}`, { defaultValue: t('FREE_PLAN.PRO_GETS.GENERIC') }) : t('FREE_PLAN.PRO_GETS.GENERIC');
  const plan = current?.planName || planName || (isFree ? t('FREE_PLAN.PLAN_FREE') : '');
  const hasUsage = typeof current?.current === 'number' && typeof current?.limit === 'number' && current.limit >= 0;
  // A trial or subscription that ended (docs/pricing-v2.md) is not a limit: it is a plan to choose.
  const ended = key === 'subscription';

  return (
    <Dialog open={!!current} onOpenChange={(open) => !open && close()}>
      <DialogContent className="sm:max-w-md" data-testid="plan-limit-dialog">
        <DialogHeader>
          <DialogTitle>{ended ? t('PRICING.DIALOG.ENDED_TITLE') : t('FREE_PLAN.DIALOG.TITLE', { plan, what })}</DialogTitle>
          <DialogDescription className="leading-relaxed">
            {hasUsage && (
              <span className="block tabular-nums">
                {t('FREE_PLAN.DIALOG.USAGE', { current: formatNumber(current!.current), limit: formatNumber(current!.limit) })}
              </span>
            )}
            <span className="block">{ended ? t('PRICING.DIALOG.ENDED_BODY') : proGets}</span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" className="text-muted-foreground" onClick={close}>
            {t('FREE_PLAN.NOT_NOW')}
          </Button>
          <Button
            variant="outline"
            data-testid="plan-limit-dialog-cta"
            onClick={() => {
              close();
              upgrade(ended ? undefined : key);
            }}
          >
            {ended ? t('PRICING.TRIAL.CHOOSE_PLAN') : t('FREE_PLAN.DIALOG.CTA')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
